import type { ComponentType, ReactNode } from 'react';
import { AlertTriangle, Inbox } from 'lucide-react';
import { DisclosurePanel } from './DisclosurePanel';

/**
 * Skeleton rows for table-shaped loading. Cheap visual placeholder —
 * stays out of the way once content arrives. Default 8 rows; tune via prop.
 */
export function LoadingShell({ rows = 8 }: { rows?: number }) {
  return (
    <div className="space-y-2" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="h-10 animate-pulse rounded-md bg-accent"
          style={{ animationDelay: `${i * 40}ms` }}
        />
      ))}
    </div>
  );
}

const DEFAULT_ERROR_TITLE = "Couldn't load this page.";

/**
 * True iff `error` looks like the API's `{ error: 'Unauthorized' }` 401 body
 * (throwIfNotOk/unwrap in both apps' lib/api.ts surface that `error` field as
 * `Error.message` verbatim) — i.e. the caller has no valid session, not a
 * generic failure. Exact match, not a substring test: a real failure whose
 * message happens to mention "unauthorized" in passing (rare, but plausible
 * from a third-party dependency) should not be relabeled as a sign-in
 * prompt.
 */
function isUnauthorizedError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : undefined;
  return message?.toLowerCase() === 'unauthorized';
}

/**
 * Inline error placard with retry. The raw `error` never renders by
 * default (SIL-539) — a finance page showing a stack-ish message beneath
 * numbers a reader is about to act on teaches nothing and undermines the
 * figures above it. It's opt-in behind a "Details" `DisclosurePanel`;
 * retry stays visible without expanding it, since that's the useful part.
 *
 * `title` defaults to the Voice-rule shape (`Couldn't load this page.`)
 * from apps/customer/DESIGN.md — call sites should pass
 * `Couldn't load <thing>.` for a specific surface.
 *
 * 2026-09-20 live-site audit finding: a signed-out visitor who lands on an
 * authenticated surface (e.g. a bookmarked/shared link, or a race before the
 * Hosted-UI redirect fires) used to see this bare default title with the raw
 * "Unauthorized" string sitting behind Details — technically correct, not
 * actionable. When the caller left `title` at its default AND the error is
 * that exact 401 body, swap in a plain-language sign-in prompt instead; the
 * raw detail still lives behind Details for support, same as before. A
 * caller that passed its own `title` is left alone — it already chose a
 * surface-specific message and knows better than this generic fallback.
 */
export function ErrorState({
  title = DEFAULT_ERROR_TITLE,
  error,
  onRetry,
  signInHref,
}: {
  title?: string;
  error?: unknown;
  onRetry?: () => void;
  /** Rendered as a "Sign in" link when the default title is showing AND the
   * error is the "Unauthorized" 401 body. Omit to suppress the link even in
   * that case (e.g. a surface with no sensible sign-in destination). */
  signInHref?: string;
}) {
  const message = error instanceof Error ? error.message : error ? String(error) : undefined;
  const unauthorized = title === DEFAULT_ERROR_TITLE && isUnauthorizedError(error);
  const displayTitle = unauthorized ? 'You need to sign in to view this page.' : title;

  return (
    <div className="flex flex-col items-center justify-center rounded-md border border-red-200 bg-red-50 px-6 py-10 text-center">
      <AlertTriangle className="size-6 text-red-500" />
      <div className="mt-3 text-sm font-medium text-red-800">{displayTitle}</div>
      {unauthorized && signInHref && (
        <a href={signInHref} className="mt-2 text-sm text-brand-600 underline">
          Sign in
        </a>
      )}
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 rounded-md border border-border bg-muted px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent"
        >
          Retry
        </button>
      )}
      {message && (
        <DisclosurePanel title="Details" className="mt-4 w-full max-w-md text-left">
          <div className="font-num text-xs text-red-700/80">{message}</div>
        </DisclosurePanel>
      )}
    </div>
  );
}

/**
 * Neutral empty-state placard. Use for "no matching results" or
 * "no data yet" surfaces. Keeps copy concise; long explanations belong
 * in tooltips/help docs. `icon` defaults to a generic inbox glyph —
 * override for a more specific empty state (e.g. `Package`, `Scale`).
 *
 * Per apps/customer/DESIGN.md's Voice section ("Empty states carry a
 * reason and an action, as text - not just a graphic and a button
 * label"): `description` (the reason) and `action` are both effectively
 * required for a call site to be in system — a bare title with neither is
 * out of system even though the props stay optional at the type level for
 * the rare list-page case where the nav label already is the reason.
 */
export function EmptyState({
  title,
  description,
  action,
  icon: Icon = Inbox,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: ComponentType<{ className?: string }>;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-border bg-card/50 px-6 py-12 text-center">
      <Icon className="size-7 text-muted-foreground" />
      <div className="mt-3 text-sm font-medium text-foreground">{title}</div>
      {description && <div className="mt-1 max-w-md text-xs text-muted-foreground">{description}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
